import React, { Fragment, useEffect, useState } from 'react';
import styled from 'styled-components';

import {
  Box,
  CircularProgress,
  Divider,
  ListItem,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography
} from '@material-ui/core';
import FolderIcon from '@material-ui/icons/Folder';

import { FetchError } from '../../common/api/api';
import { formatDate, formatSize } from '../../common/formatter';
import { useUserContext } from '../../user/User.context';
import UserLink from '../users/components/UserLink';
import DocumentGroupSelector from './components/DocumentGroupSelector';
import { useGetDocumentHistory } from './Document.hook';
import { FileDto, FileLogDto } from './Document.interface';

export interface InfoDrawerTabPanelProps {
  document: FileDto;
  onChange?: (value: FileDto) => void;
  onError?: (error: FetchError) => void;
}

enum TabPanels {
  Details = 0,
  History = 1
}

const DocumentInfoDrawerTabPanel: React.FC<InfoDrawerTabPanelProps> = props => {
  const { document, onChange, onError } = props;
  const [index, setIndex] = useState(0);
  const [history, setHistory] = useState<FileLogDto[]>([]);
  const [historyFetched, setHistoryFetched] = useState<boolean>(false);
  const can = useUserContext().permissions.file;
  const {
    data: paginatedData,
    isLoading,
    error,
    get: fetchHistory
  } = useGetDocumentHistory(document.id);
  const data: FileLogDto[] | undefined = paginatedData?.data;
  const [maxHeight, setMaxHeight] = useState<number>(window.innerHeight - 265);
  const isAdmin = can.update && can.delete;
  const isTrash = document.name === 'Trash' && document.isLocked;
  const canConfigure = document.isFolder && isAdmin && !isTrash;

  useEffect(() => {
    const handleResize = () => {
      setMaxHeight(window.innerHeight - 265);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setIndex(0);
    setHistoryFetched(false);
  }, [document]);

  useEffect(() => {
    if (data) {
      setHistory(data);
      setHistoryFetched(true);
    }
  }, [data]);

  const handleTabChange = (event: React.ChangeEvent<{}>, tabIndex: number) => {
    if (tabIndex === TabPanels.History && !historyFetched) {
      fetchHistory();
    }
    setIndex(tabIndex);
  };

  return (
    <Fragment>
      <Tabs
        value={index}
        indicatorColor='primary'
        scrollButtons='auto'
        onChange={handleTabChange}
      >
        <Tab label='Details' {...TabProps(TabPanels.Details)} />
        <Tab label='History' {...TabProps(TabPanels.History)} />
      </Tabs>
      <Divider />
      <TabPanel value={index} index={0}>
        <StyledList>
          {!document.isFolder && (
            <ListItem>
              <StyledFirstItem>ID</StyledFirstItem>
              <StyledSecondItem>
                {document.task?.key || `DOC-${document.id}`}
              </StyledSecondItem>
            </ListItem>
          )}
          <ListItem>
            <StyledFirstItem>Type</StyledFirstItem>
            <StyledSecondItem>
              {document.isFolder ? 'Folder' : 'File'}
            </StyledSecondItem>
          </ListItem>
          <ListItem>
            <StyledFirstItem>Size</StyledFirstItem>
            <StyledSecondItem>
              {formatSize(document.size || 0)}
            </StyledSecondItem>
          </ListItem>
          <ListItem>
            <StyledFirstItem>Location</StyledFirstItem>
            <StyledSecondItem>
              <div style={{ float: 'left' }}>
                <FolderIcon />
              </div>
              <div className={'location-path'}>{document.path}</div>
            </StyledSecondItem>
          </ListItem>
          <ListItem>
            <StyledFirstItem>Owner</StyledFirstItem>
            <StyledSecondItem>
              <UserLink type='name' user={document.ownedBy || undefined} />
            </StyledSecondItem>
          </ListItem>
          <ListItem>
            <StyledFirstItem>Modified</StyledFirstItem>
            <StyledSecondItem>
              {formatDate(document.modified)}{' '}
              {document.modifiedBy && (
                <Fragment>
                  by <UserLink type='name' user={document.modifiedBy} />
                </Fragment>
              )}
            </StyledSecondItem>
          </ListItem>
          <ListItem>
            <StyledFirstItem>Created</StyledFirstItem>
            <StyledSecondItem>
              {formatDate(document.created)}{' '}
              {document.createdBy && (
                <Fragment>
                  by <UserLink type='name' user={document.createdBy} />
                </Fragment>
              )}
            </StyledSecondItem>
          </ListItem>
          {canConfigure && (
            <>
              <Box paddingY={1}>
                <Divider />
              </Box>
              <Box className='settings'>
                <Box className='settings-title'>
                  <Typography variant='overline' color='textSecondary'>
                    Settings
                  </Typography>
                </Box>
                <DocumentGroupSelector
                  file={document}
                  onChange={onChange}
                  onError={onError}
                />
              </Box>
            </>
          )}
        </StyledList>
      </TabPanel>
      <TabPanel value={index} index={1}>
        <Paper>
          <StyledTableContainer height={maxHeight}>
            <Table stickyHeader size='small'>
              <StyledTableHead>
                <TableRow>
                  <TableCell>History</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </StyledTableHead>

              <TableBody>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={3}>
                      <Box
                        height={300}
                        width='100%'
                        display='flex'
                        alignItems='center'
                        justifyContent='center'
                      >
                        <CircularProgress />
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                {error && (
                  <TableRow>
                    <TableCell colSpan={3}>Error loading history</TableCell>
                  </TableRow>
                )}
                {history &&
                  history.map((item: FileLogDto) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.log}</TableCell>
                      <TableCell>
                        <UserLink type='name' user={document.createdBy} />
                      </TableCell>
                      <TableCell>{formatDate(item.created)}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </StyledTableContainer>
        </Paper>
      </TabPanel>
    </Fragment>
  );
};

const StyledDiv = styled.div`
  flex: 1;
  width: 100%;
  overflow-y: auto;
`;

const StyledFirstItem = styled(Box)`
  flex: 2;
  font-weight: 300;
`;

const StyledSecondItem = styled(Box)`
  flex: 3;
`;

const StyledTableContainer = styled(TableContainer)<{ height: number }>`
  max-height: ${p => p.height}px;
  table {
    max-width: 320;
    white-space: 'normal';
    text-transform: capitalize;
    tr {
      height: 50px;
    }
  }
`;

const StyledTableHead = styled(TableHead)`
  text-transform: uppercase;
  font-weight: 'bold';
  color: '#7c7c7c';
  min-height: 56px;
`;

const StyledList = styled(Box)`
  font-size: 13px;
  padding-top: 10px;
  padding-bottom: 20px;
  .location-path {
    margin-left: 30px;
    margin-top: 3px;
  }
  li {
    line-height: 2em;
  }
  .settings {
    padding-left: 16px;
    padding-right: 16px;
    padding-bottom: 64px;
    .settings-title {
      margin-bottom: 8px;
    }
  }
`;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = props => {
  const { children, value, index } = props;
  return (
    <StyledDiv
      role='tabpanel'
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
    >
      {value === index && children}
    </StyledDiv>
  );
};

function TabProps(index: any) {
  return {
    id: `tab-${index}`,
    'aria-controls': `tabpanel-${index}`
  };
}

export default DocumentInfoDrawerTabPanel;
